import FingerprintJS from '@fingerprintjs/fingerprintjs'
import Toast from 'antd-mobile/es/components/toast'
import CryptoJS from 'crypto-js'
import qs from 'query-string'
import {useLocation} from 'react-router'

import {OsType} from '~/types/bussiness/callApp'

import {LOCAL_STORAGE_KEY_DID} from './constant'
import {getStorageItem, setStorageItem} from './storage'

/**
 * 获取浏览器指纹
 * @returns 浏览器指纹
 */
export async function getBrowserFingerprint() {
  const cachedFingerprint = getStorageItem(LOCAL_STORAGE_KEY_DID) || ''
  // 如果已经生成过指纹，直接返回
  if (cachedFingerprint) {
    return cachedFingerprint
  }

  // 初始化 FingerprintJS
  const fp = await FingerprintJS.load()
  // 获取指纹
  const result = await fp.get()
  // 缓存指纹
  setStorageItem(LOCAL_STORAGE_KEY_DID, result.visitorId)

  return result.visitorId
}

/**
 * 生成安全的 nonce
 * 优先级：Web Crypto API > crypto-js > 后备方案
 */
export const generateSecureNonce = async (): Promise<string> => {
  // 方案 1：使用 Web Crypto API（最安全）
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      const randomBytes = new Uint8Array(16)
      crypto.getRandomValues(randomBytes)

      // 转换为十六进制
      const hexString = Array.from(randomBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')

      return hexString
    } catch (error) {
      console.warn('Web Crypto API 生成 nonce 失败，使用后备方案', error)
    }
  }

  // 方案 2：使用 crypto-js（兼容性好）
  try {
    const randomString = [
      Math.random().toString(36),
      Date.now().toString(36),
      Math.random().toString(36),
      typeof performance !== 'undefined'
        ? performance.now().toString(36)
        : Math.random().toString(36),
    ].join('')

    const hashedNonce = CryptoJS.SHA256(randomString).toString(CryptoJS.enc.Hex)

    return hashedNonce.substring(0, 32)
  } catch (error) {
    console.error('生成 nonce 失败', error)
    // 最终后备方案
    return `${Date.now()}-${Math.random().toString(36)}`
  }
}

/**
 * 获取当前环境
 * @returns 'development' | 'production' | 'test'
 */
export const getEnvironment = (): 'development' | 'production' | 'test' => {
  console.log('ENV========>', ENV)
  return ENV as 'development' | 'production' | 'test'
}

/**
 * 判断是否是开发环境
 * @returns boolean
 */
export const isDevelopment = (): boolean => {
  return ENV === 'development'
}

/**
 * 判断是否是生产环境
 * @returns boolean
 */
export const isProduction = (): boolean => {
  return ENV === 'production'
}

/**
 * 使代码暂停指定的毫秒数
 * @param ms - 暂停的毫秒数
 * @returns Promise
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * 在下一个空闲时间执行回调函数
 * @param fn - 要执行的回调函数
 */
export const nextTick = (fn: IdleRequestCallback) => {
  const callback =
    typeof window !== 'undefined' &&
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (e: IdleRequestCallback) => {
          const n = Date.now()

          return setTimeout(() => {
            e({
              didTimeout: !1,
              timeRemaining() {
                return Math.max(0, 50 - (Date.now() - n))
              },
            })
          }, 1)
        }

  callback(fn, {timeout: 300})
}

/**
 * 检测移动端操作系统
 * @returns 'iOS' | 'Android' | 'Other'
 */
export const getMobileOS = (): OsType => {
  const {userAgent} = navigator
  // 检查iOS
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'ios'
  }

  // 检查Android
  if (/android/i.test(userAgent)) {
    return 'android'
  }

  // 其他环境
  return 'other'
}

/**
 * 判断是否在浏览器环境中运行
 * @returns boolean
 */
export function isBrowser() {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  )
}

/**
 * 分割数字
 * @param num 数字
 * @returns 分割后的数字
 */
export function splitPriceNumber(num: number) {
  // 将数字转换为字符串
  const numStr = num.toString()
  // 计算分割点
  const splitIndex = numStr.length - 2 // 取倒数第二位作为分割点

  // 分割字符串
  const part1 = numStr.slice(0, splitIndex) || '0'
  const part2 = numStr.slice(splitIndex)

  // 返回结果
  return [part1, part2]
}

export function onVisibilityChange(fn) {
  let hidden
  let visibilityChange
  if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden'
    visibilityChange = 'visibilitychange'
  } else if (typeof (document as any).msHidden !== 'undefined') {
    hidden = 'msHidden'
    visibilityChange = 'msvisibilitychange'
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    hidden = 'webkitHidden'
    visibilityChange = 'webkitvisibilitychange'
  }

  function handleVisibilityChange() {
    fn(document[hidden])
  }

  document.addEventListener(visibilityChange, handleVisibilityChange, false)

  const un = () => {
    document.removeEventListener(
      visibilityChange,
      handleVisibilityChange,
      false
    )
  }

  return un
}

export function useQuery<T extends Record<string, unknown>>() {
  const {search} = useLocation()
  return qs.parse(search) as unknown as T
}

export function formatVersion(versionStr = '') {
  const verStr = versionStr.split('.')

  //  补充到5个版本 ['11','2','3','001'] => ['11','2','3','001','']
  while (verStr.length < 5) verStr.push('')

  // 每个版本位置补充到4位  ['11','2','3',''] => ['0011','0002','0003','0000']
  const fillVerStr = verStr.map((v) => v.padStart(4, '0'))

  // 返回数字化的版本号 ['0011','0002','0003','0000'] => 11000200030000
  return parseInt(fillVerStr.join(''), 10)
}

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 */
export const copyTextToClipboard = async (
  text: string,
  successText?: string
) => {
  try {
    if (!navigator.clipboard) {
      throw new Error('浏览器不支持Clipboard API')
    }
    await navigator.clipboard.writeText(text)
    Toast.show(successText || '复制成功')
  } catch (err) {
    // 回退到execCommand方案
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    Toast.show(successText || '已复制')
  }
}

export function getImageUrl(id: number) {
  return isProduction()
    ? `https://file.aimoox.com/account/view/id/${id}/sz/src`
    : `http://file-test.aimoox.com/account/view/id/${id}/sz/src`
}

/**
 * 格式化数字，大于1000转换为k
 * @param num 要格式化的数字
 * @returns 格式化后的字符串
 */
export function formatNumber(num: number | undefined | null): string {
  if (num === undefined || num === null) {
    return '0'
  }
  if (num >= 1000) {
    const k = num / 1000
    return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`
  }

  return num.toString()
}
